import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  ArrowLeft,
  Images,
  Search,
} from "lucide-react";

import {
  useNavigate,
  useParams,
} from "react-router-dom";

import { useAuth } from "../../../../contexts/AuthContext";
import { getAccessToken } from "../../../../awsAuth";

import PhotoDetails from "../../../PhotoDetails/PhotoDetails";

import "./BeforeAfterAll.scss";

const API_BASE =
  process.env.REACT_APP_API_URL ||
  "https://cvanto-api.onrender.com";

function makeFallbackId(index) {
  return `before_after_${index}`;
}

function normalizeItems(items) {
  return (Array.isArray(items) ? items : [])
    .map((item, index) => ({
      id: String(
        item?.id ||
          makeFallbackId(index)
      ),

      title: String(
        item?.title || ""
      ).trim(),

      beforeUrl: String(
        item?.beforeUrl || ""
      ).trim(),

      afterUrl: String(
        item?.afterUrl || ""
      ).trim(),

      createdAt:
        item?.createdAt || "",

      updatedAt:
        item?.updatedAt || "",
    }))
    .filter(
      (item) =>
        item.beforeUrl ||
        item.afterUrl
    );
}

export default function BeforeAfterAll() {
  const navigate = useNavigate();
  const params = useParams();
  const { userId } = useAuth();

  const viewedUserId =
    params.userId ||
    params.id ||
    userId;

  const [items, setItems] =
    useState([]);

  const [
    businessName,
    setBusinessName,
  ] = useState("До / Після");

  const [query, setQuery] =
    useState("");

  const [loading, setLoading] =
    useState(true);

  const [error, setError] =
    useState("");

  const [
    photoViewer,
    setPhotoViewer,
  ] = useState({
    open: false,
    photos: [],
    startIndex: 0,
  });

  useEffect(() => {
    let alive = true;

    async function loadBeforeAfter() {
      if (!viewedUserId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError("");

        const token =
          await getAccessToken()
            .catch(() => null);

        const headers = token
          ? {
              Authorization:
                `Bearer ${token}`,
            }
          : {};

        const isOwnProfile =
          userId &&
          String(viewedUserId) ===
            String(userId);

        const beforeAfterUrl =
          isOwnProfile
            ? `${API_BASE}/api/business/before-after/me`
            : `${API_BASE}/api/business/before-after/user/${encodeURIComponent(
                viewedUserId
              )}`;

        const profileUrl =
          isOwnProfile
            ? `${API_BASE}/api/business/profile/me`
            : `${API_BASE}/api/business/profile/${encodeURIComponent(
                viewedUserId
              )}`;

        const [
          beforeAfterResponse,
          profileResponse,
        ] = await Promise.all([
          fetch(beforeAfterUrl, {
            headers,
          }),

          fetch(profileUrl, {
            headers,
          }),
        ]);

        const beforeAfterData =
          await beforeAfterResponse
            .json()
            .catch(() => ({}));

        const profileData =
          await profileResponse
            .json()
            .catch(() => ({}));

        if (!beforeAfterResponse.ok) {
          throw new Error(
            beforeAfterData?.error ||
              "Не вдалося завантажити кейси"
          );
        }

        if (!alive) return;

        const normalizedItems =
          normalizeItems(
            beforeAfterData?.items || []
          );

        const userBase =
          profileData?.userBase || {};

        const profile =
          profileData?.profile || {};

        setItems(normalizedItems);

        setBusinessName(
          userBase.username ||
            profile.title ||
            profile.hero?.title ||
            "До / Після"
        );
      } catch (err) {
        console.error(
          "[BeforeAfterAll] load error:",
          err
        );

        if (alive) {
          setError(
            err.message ||
              "Помилка завантаження"
          );
        }
      } finally {
        if (alive) {
          setLoading(false);
        }
      }
    }

    loadBeforeAfter();

    return () => {
      alive = false;
    };
  }, [viewedUserId, userId]);

  const filteredItems =
    useMemo(() => {
      const safeItems =
        Array.isArray(items)
          ? [...items]
          : [];

      const normalizedQuery =
        query
          .trim()
          .toLowerCase();

      if (!normalizedQuery) {
        return safeItems;
      }

      return safeItems.filter(
        (item) =>
          String(item.title || "")
            .toLowerCase()
            .includes(
              normalizedQuery
            )
      );
    }, [items, query]);

  const openCasePhotos = (
    item,
    requestedIndex = 0
  ) => {
    const photos = [
      item.beforeUrl
        ? {
            src: item.beforeUrl,
            alt: `${
              item.title ||
              "Результат роботи"
            } — До`,
          }
        : null,

      item.afterUrl
        ? {
            src: item.afterUrl,
            alt: `${
              item.title ||
              "Результат роботи"
            } — Після`,
          }
        : null,
    ].filter(Boolean);

    if (!photos.length) return;

    const safeStartIndex =
      Math.max(
        0,
        Math.min(
          requestedIndex,
          photos.length - 1
        )
      );

    setPhotoViewer({
      open: true,
      photos,
      startIndex:
        safeStartIndex,
    });
  };

  const closePhotoViewer = () => {
    setPhotoViewer(
      (current) => ({
        ...current,
        open: false,
      })
    );
  };

  if (loading) {
    return (
      <main className="beforeAfterAll">
        <div className="beforeAfterAll__state">
          Завантаження кейсів...
        </div>
      </main>
    );
  }

  if (error) {
    return (
      <main className="beforeAfterAll">
        <div className="beforeAfterAll__state beforeAfterAll__state--error">
          {error}
        </div>
      </main>
    );
  }

  return (
    <main className="beforeAfterAll">
      <header className="beforeAfterAll__header">
        <button
          type="button"
          className="beforeAfterAll__back"
          onClick={() =>
            navigate(-1)
          }
          aria-label="Назад"
        >
          <ArrowLeft
            size={22}
            strokeWidth={2.4}
          />
        </button>

        <div className="beforeAfterAll__heading">
          <span>
            Бізнес-профіль
          </span>

          <h1>До / Після</h1>

          <p>
            {businessName} ·{" "}
            {items.length}{" "}
            {items.length === 1
              ? "кейс"
              : items.length >= 2 &&
                items.length <= 4
              ? "кейси"
              : "кейсів"}
          </p>
        </div>
      </header>

      <section className="beforeAfterAll__search">
        <Search
          size={18}
          strokeWidth={2.4}
        />

        <input
          value={query}
          onChange={(event) =>
            setQuery(
              event.target.value
            )
          }
          placeholder="Пошук за назвою"
        />
      </section>

      {!filteredItems.length ? (
        <section className="beforeAfterAll__empty">
          <span className="beforeAfterAll__emptyIcon">
            <Images
              size={26}
              strokeWidth={2.2}
            />
          </span>

          <strong>
            Кейсів не знайдено
          </strong>

          <p>
            Спробуйте змінити
            пошуковий запит.
          </p>
        </section>
      ) : (
        <section className="beforeAfterAll__grid">
          {filteredItems.map(
            (item) => (
              <article
                key={item.id}
                className="beforeAfterAllCard"
              >
                <div className="beforeAfterAllCard__compare">
                  <button
                    type="button"
                    className="beforeAfterAllCard__photo"
                    onClick={() =>
                      openCasePhotos(
                        item,
                        0
                      )
                    }
                    aria-label="Переглянути фото до"
                  >
                    {item.beforeUrl ? (
                      <img
                        src={
                          item.beforeUrl
                        }
                        alt={`${
                          item.title ||
                          "Результат роботи"
                        } — До`}
                        loading="lazy"
                        decoding="async"
                      />
                    ) : (
                      <span className="beforeAfterAllCard__placeholder">
                        Фото відсутнє
                      </span>
                    )}

                    <em>До</em>
                  </button>

                  <button
                    type="button"
                    className="beforeAfterAllCard__photo"
                    onClick={() =>
                      openCasePhotos(
                        item,
                        item.beforeUrl
                          ? 1
                          : 0
                      )
                    }
                    aria-label="Переглянути фото після"
                  >
                    {item.afterUrl ? (
                      <img
                        src={
                          item.afterUrl
                        }
                        alt={`${
                          item.title ||
                          "Результат роботи"
                        } — Після`}
                        loading="lazy"
                        decoding="async"
                      />
                    ) : (
                      <span className="beforeAfterAllCard__placeholder">
                        Фото відсутнє
                      </span>
                    )}

                    <em>Після</em>
                  </button>
                </div>

                <div className="beforeAfterAllCard__bottom">
                  <strong>
                    {item.title ||
                      "Результат роботи"}
                  </strong>

                  <span>
                    Натисніть на фото,
                    щоб роздивитися
                  </span>
                </div>
              </article>
            )
          )}
        </section>
      )}

      <PhotoDetails
        isOpen={
          photoViewer.open
        }
        photos={
          photoViewer.photos
        }
        startIndex={
          photoViewer.startIndex
        }
        onClose={
          closePhotoViewer
        }
      />
    </main>
  );
}